import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Badge } from '@/components/ui/PageHeader';
import { growth, type LocationDraft, type Campaign, type ReviewCommand } from '@/api/provider-growth';
import { useGrowthCommand } from '../providers/useGrowthCommand';
import { useAuth } from '@/context/AuthContext';
import type { ApiResponse } from '@/api/types';

export function ProviderGrowthReviewPanel() {
  const { user } = useAuth();
  const drafts = useQuery({ queryKey: ['growth-review-drafts', user?.id], queryFn: growth.pendingDrafts, retry: false });
  const campaigns = useQuery({ queryKey: ['growth-review-campaigns', user?.id], queryFn: growth.pendingCampaigns, retry: false });
  return <section className="space-y-4" aria-label="Provider publication review">
    <h2 className="font-semibold">Location and campaign review</h2>
    <p className="text-sm text-gray-600">These queues are separate from identity verification. Approval returns the item to its owner for explicit publication.</p>
    <Button variant="secondary" onClick={() => { void drafts.refetch(); void campaigns.refetch(); }}>Refresh review queues</Button>
    <h3 className="text-sm font-semibold">Pending locations</h3>
    {drafts.isLoading && <p role="status">Loading locations…</p>}
    {drafts.error && <p role="alert">{drafts.error.message}</p>}
    {drafts.isSuccess && !drafts.data.data.drafts.length && <p className="text-sm">No locations await review.</p>}
    {(drafts.data?.data.drafts ?? []).map((draft) => <ReviewItem key={draft.id + ':' + draft.revision} kind="location" item={draft} />)}
    <h3 className="text-sm font-semibold">Pending campaigns</h3>
    {campaigns.isLoading && <p role="status">Loading campaigns…</p>}
    {campaigns.error && <p role="alert">{campaigns.error.message}</p>}
    {campaigns.isSuccess && !campaigns.data.data.campaigns.length && <p className="text-sm">No campaigns await review.</p>}
    {(campaigns.data?.data.campaigns ?? []).map((campaign) => <ReviewItem key={campaign.id + ':' + campaign.revision} kind="campaign" item={campaign} />)}
  </section>;
}
function ReviewItem(props: { kind: 'location'; item: LocationDraft } | { kind: 'campaign'; item: Campaign }) {
  const qc = useQueryClient();
  const command = useGrowthCommand();
  const [decision, setDecision] = useState<ReviewCommand['decision']>('request_changes');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [done, setDone] = useState(false);
  async function review() {
    const facts = { expectedRevision: props.item.revision, decision, reasonCode: reason.trim(), memberMessage: message.trim() };
    const response = await command.run<ApiResponse<LocationDraft | Campaign>>('review-' + props.kind, { id: props.item.id, ...facts }, (idempotencyKey) =>
      props.kind === 'location'
        ? growth.reviewDraft(props.item.id, { ...facts, idempotencyKey })
        : growth.reviewCampaign(props.item.id, { ...facts, idempotencyKey }));
    if (response) {
      setDone(true);
      command.setMessage('Review saved. Approval does not publish this item.');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['growth-review-drafts'] }),
        qc.invalidateQueries({ queryKey: ['growth-review-campaigns'] }),
        qc.invalidateQueries({ queryKey: ['growth-drafts'] }),
        qc.invalidateQueries({ queryKey: ['growth-campaigns'] }),
      ]);
    }
  }
  return <article className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
    <h4 className="font-semibold">{props.kind === 'location' ? props.item.name : props.item.headline}</h4>
    <p className="text-xs break-all"><Badge variant="gray">{props.kind}</Badge> {props.item.id} · Revision {props.item.revision}</p>
    {props.kind === 'location' ? <div className="text-sm space-y-1">
      <p>{props.item.kind} · {props.item.category.name}</p>
      <p>{props.item.address} · {props.item.city}, {props.item.state}</p>
      <p>{props.item.phone} · {props.item.website}</p>
      <p className="whitespace-pre-wrap">{props.item.description}</p>
    </div> : <div className="text-sm space-y-1">
      <p>{props.item.provider.name} · {props.item.purpose} · {props.item.funding} · {props.item.metroCode}</p>
      <p>{new Date(props.item.startsAt).toLocaleString()} — {new Date(props.item.expiresAt).toLocaleString()}</p>
      <p className="whitespace-pre-wrap">{props.item.summary}</p>
      <p className="whitespace-pre-wrap">{props.item.body}</p>
      <p className="whitespace-pre-wrap">Terms: {props.item.terms || 'None supplied'}</p>
    </div>}
    <details className="text-xs"><summary>All submitted details</summary><pre className="whitespace-pre-wrap break-all mt-2">{JSON.stringify(props.item, null, 2)}</pre></details>
    <fieldset disabled={command.busy || done} className="space-y-3">
      <label className="block text-sm">Decision<select className="input mt-1" value={decision} onChange={(event) => { setDecision(event.target.value as ReviewCommand['decision']); setConfirmed(false); }}>
        <option value="request_changes">Request changes</option><option value="approve">Approve</option>
        {props.kind === 'campaign' && <option value="reject">Reject</option>}
      </select></label>
      <label className="block text-sm">Reason code<input className="input mt-1" maxLength={80} value={reason} onChange={(event) => { setReason(event.target.value); setConfirmed(false); }} /></label>
      <label className="block text-sm">Message to the owner<textarea className="input mt-1" maxLength={500} rows={2} value={message} onChange={(event) => { setMessage(event.target.value); setConfirmed(false); }} /></label>
      <label className="flex gap-2 text-sm"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />I checked these details and confirm this review decision.</label>
      <Button disabled={!confirmed || !reason.trim() || !message.trim()} onClick={() => void review()}>Save review decision</Button>
    </fieldset>
    {command.error && <p role="alert" className="text-sm text-red-700">{command.error} Refresh the queue if this revision changed.</p>}
    {command.message && <p role="status" className="text-sm text-green-700">{command.message}</p>}
  </article>;
}
